import { useQuery, UseQueryResult } from '@tanstack/react-query'
import { labelService } from '../services/labelService'
import type { FieldLabelsResponse } from '../types/labels.types'

/**
 * React Query hook for field labels
 *
 * Features:
 * - Aggressive caching: 24h stale time, 7 day cache time
 * - Automatic background refetch after staleness
 * - Shared cache across components using same context
 * - Error handling with graceful degradation
 *
 * @param context - Table context (e.g., 'offerings', 'customers', 'costs')
 * @returns React Query result with labels data
 *
 * @example
 * ```tsx
 * function MyComponent() {
 *   const { data, isLoading, error } = useFieldLabels('offerings')
 *
 *   if (isLoading) return <Spin />
 *   if (error) return <Alert message="Failed to load labels" />
 *
 *   return <div>{data.labels.name}</div>
 * }
 * ```
 */
export function useFieldLabels(context: string): UseQueryResult<FieldLabelsResponse, Error> {
  return useQuery({
    queryKey: ['field-labels', context],
    queryFn: async () => {
      const response = await labelService.getLabels(context)

      if (!response.success || !response.data) {
        throw new Error(response.message || `Failed to fetch labels for context: ${context}`)
      }

      return response.data
    },
    staleTime: 1000 * 60 * 60 * 24,      // 24 hours - labels rarely change
    gcTime: 1000 * 60 * 60 * 24 * 7,     // 7 days - keep in cache even when unmounted
    refetchOnWindowFocus: false,          // Don't refetch when user switches tabs
    refetchOnMount: false,                // Use cached data if available
    retry: 2,                             // Retry failed requests twice
    retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000), // Exponential backoff
  })
}

export default useFieldLabels
